import {
  ORDER_STAGE_LABELS,
  type ChangeRow,
  type Json,
  type OrderStage,
} from "@estelle/domain";

import { eventValue } from "@/modules/orders/format";

/** Shopify's status keys, as the order page and the change list both say them. */
export const STATUS_LABEL: Record<string, string> = {
  pending: "Pending",
  authorized: "Authorized",
  partially_paid: "Partially paid",
  paid: "Paid",
  partially_refunded: "Partially refunded",
  refunded: "Refunded",
  voided: "Voided",
  partial: "Partially fulfilled",
  fulfilled: "Fulfilled",
  restocked: "Restocked",
  in_transit: "In transit",
  out_for_delivery: "Out for delivery",
  delivered: "Delivered",
  attempted_delivery: "Delivery attempted",
  not_delivered: "Not delivered",
  ready_for_pickup: "Ready for pickup",
  failed: "Failed",
  returned: "Returned",
  cancelled: "Cancelled",
};

/** A status key as words; no status at all is Shopify's "Unfulfilled". */
export function statusLabel(value: string | null): string {
  if (value === null) return "Unfulfilled";
  return STATUS_LABEL[value] ?? value;
}

function inner(value: Json | null, key: string): string | null {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  const found = value[key];
  return typeof found === "string" ? found : null;
}

function scalar(value: Json | null): string | null {
  return typeof value === "string" ? value : null;
}

/**
 * One change as a line a person reads (#642): "Status: Pending → Shipped",
 * "Tag added: confirmed". Recent Changes used to show the raw field key
 * ("status") and nothing of what it changed from or to.
 */
export function describeChange(
  row: Pick<ChangeRow, "eventType" | "field" | "before" | "after">,
): string {
  switch (row.eventType) {
    case "created":
      return "Order placed";
    // The tag is written on whichever side the writer chose; read either.
    case "tag_added":
      return `Tag added: ${eventValue(row.after ?? row.before)}`;
    case "tag_removed":
      return `Tag removed: ${eventValue(row.before ?? row.after)}`;
    case "note_changed":
      return row.after === null || row.after === ""
        ? "Note cleared"
        : `Note: ${eventValue(row.after)}`;
    case "financial_status_changed":
      return `Payment status: ${statusLabel(scalar(row.before))} → ${statusLabel(scalar(row.after))}`;
    case "fulfilment_status_changed":
      return `Fulfilment status: ${statusLabel(scalar(row.before))} → ${statusLabel(scalar(row.after))}`;
    case "shipment_created": {
      const courier = inner(row.after, "courier");
      return courier ? `Shipment created with ${courier}` : "Shipment created";
    }
    case "shipment_updated": {
      if (row.field === "status") {
        const before = inner(row.before, "status");
        const after = inner(row.after, "status");
        return before
          ? `Status: ${statusLabel(before)} → ${statusLabel(after)}`
          : `Status: ${statusLabel(after)}`;
      }
      return row.field === "tracking" ? "Tracking updated" : "Shipment updated";
    }
    case "cancelled": {
      const reason = inner(row.after, "reason");
      return reason ? `Cancelled: ${reason.toLowerCase()}` : "Cancelled";
    }
    case "refunded":
      return "Refunded";
    case "address_changed":
      return "Shipping address changed";
    case "line_item_changed":
      return `Items: ${eventValue(row.before)} → ${eventValue(row.after)}`;
    case "rto_received":
      return "Return received";
    case "possible_double_restock":
      return "Shopify restocked after the ERP did";
    case "return_added_manually":
      return "Return added by hand";
    case "stage_override_set": {
      const stage = inner(row.after, "stage");
      return stage
        ? `Stage set by hand: ${ORDER_STAGE_LABELS[stage as OrderStage] ?? stage}`
        : "Stage set by hand";
    }
    case "stage_override_cleared":
      return "Stage back to automatic";
  }
}

/**
 * Who made a change (#642): "Shopify" for what the store did, the person's
 * name for an ERP write. A dash used to stand for Shopify.
 */
export function changedBy(
  row: Pick<ChangeRow, "source" | "actorUserId" | "actorName">,
): string {
  if (row.source === "channel") return "Shopify";
  return row.actorName ?? row.actorUserId ?? "Someone in the ERP";
}
